/**
 * GalleryAppearanceSection.tsx - Gallery Visual Appearance Configuration
 *
 * This component provides configuration options for the visual appearance
 * of the public fundraiser gallery including branding, colors, and layout.
 *
 * Key Features:
 * - Gallery title and branding customization
 * - Logo upload and management with icon fallback
 * - Color scheme configuration for gallery theming
 * - Background and text color customization
 * - Icon selection and color theming
 * - Real-time preview of appearance changes
 *
 * Appearance Settings:
 * - Gallery Title: Custom title for the public gallery
 * - Logo/Icon: Upload custom logo or select from icon library
 * - Background Color: Gallery page background color
 * - Text Color: Primary text color for readability
 * - Accent Colors: Highlight and accent color theming
 *
 * Logo Management:
 * - Image upload with format validation
 * - Icon selection from Lucide icon library
 * - Icon color customization and theming
 * - Fallback handling for missing images
 * - Size optimization and responsive display
 *
 * Color Configuration:
 * - Color picker integration for precise selection
 * - Preset color templates for quick setup
 * - Accessibility validation for color contrast
 * - Real-time preview of color changes
 *
 * Props: None (uses MobX store for gallery settings)
 *
 * Dependencies: Color picker, icon selector, image upload, MobX store
 */

import React, { useState } from "react";
import styled from "styled-components";
import { observer } from "mobx-react-lite";
import { Palette, X, Plus, Heart } from "lucide-react";
import * as Icons from "lucide-react";
import { fundraiserStore } from "../../stores/FundraiserStore";
import { IconSelectorModal } from "../fundraiser-builder/IconSelectorModal";
import { ColorModel, GallerySettingsModel } from "../../models/GalleryModels";
import { IconType } from "../../enums";
import {
  FlexContainer,
  IColor,
  ColorPicker as ColorPickerComponent,
} from "@mindmemobile/mm-shared-global";
import { darkenColor, isLightColor } from "../../utils";
interface IProps {
  color?: string;
  isActive?: boolean;
  pillShape?: boolean;
  roundedRect?: boolean;
  heightWidth?: string;
  padding?: string;
  borderRadius?: string;
  active?: boolean;
  cursor?: string;
}
export const SelectedColorButton = styled.div<IProps>`
  height: ${(props) => props.heightWidth || "40px"};
  min-width: ${(props) => props.heightWidth || "40px"};
  padding: ${(props) => props.padding || "5px"};
  border: 2px solid ${(props) => (props.active ? "#57ac2d" : "#cccccc")};
  border-radius: ${(props) => props.borderRadius || "4px"};
  cursor: ${(props) => props.cursor || "pointer"};
  text-align: center;
  line-height: 28px;
  div {
    height: 100%;
    width: 100%;
    background-color: ${(props) => props.color};
    border-radius: ${(props) => props.borderRadius || "4px"};
    cursor: pointer;
  }
  img {
    height: 100%;
    width: 100%;
  }
  &.opaque-unselected {
    opacity: ${(props) => (props.active ? "1" : "0.65")};
    cursor: not-allowed;
  }
  &.opaque-selected {
    opacity: ${(props) => (props.active ? "1" : "0.65")};
    cursor: pointer;
  }
  &.colored-btn-as-input {
    height: auto;
    min-height: 40px;
    max-width: 140px;
    > div {
      min-height: 28px;
      height: auto;
      cursor: pointer;
    }
  }
  &.opaque-btn-as-input {
    height: auto;
    min-height: 40px;
    max-width: 140px;
    > div {
      min-height: 28px;
      height: auto;
      cursor: not-allowed;
    }
  }
  &.color-picker-gallery {
    height: 44px;
    width: 44px;
    border-radius: 8px;
    border-color: #e2e8f0;
    padding: 0px;
    &:focus {
      outline: none;
    }
    > div {
      border-radius: 6px;
    }
    & + .checkout-color-picker {
      position: absolute;
      top: 0;
      left: 0;
      transform: translate(-1px, 40px);
      z-index: 1001 !important;
      #rbgcp-wrapper {
        // this is for the height width of the color picker box inside the color picker component
        > div:first-child > div:nth-child(2) > div:nth-child(2) {
          height: 150px !important;
        }
        > div:first-child > div:first-child {
          height: 165px !important;
        }
      }
    }
    &.detail-page-color-picker {
      & + .checkout-color-picker {
        transform: translate(55px, -1px);
      }
    }
  }
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 52px; /* Increased from 32px to 52px (32 + 20) */
`;

const SectionTitle = styled.h3`
  font-size: 24px;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-size: 16px;
  font-weight: 600;
  color: #374151;
`;

const Input = styled.input`
  padding: 16px;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  font-size: 16px;
  color: #1e293b;
  transition: border-color 0.2s ease;
  background: #ffffff;
  width: 400px;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const InputWithCounter = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 400px;
`;

const CharacterCounter = styled.div`
  font-size: 12px;
  color: #6b7280;
  text-align: right;
`;

const ColorSection = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 70px; /* Increased from 60px to 70px */
  .checkout-color-picker {
    position: absolute;
    top: 0;
    left: 0;
    transform: translate(-1px, 40px);
    z-index: 1001 !important;

    #rbgcp-wrapper {
      // this is for the height width of the color picker box inside the color picker component
      > div:first-child > div:nth-child(2) > div:nth-child(2) {
        height: 150px !important;
      }
      > div:first-child > div:first-child {
        height: 165px !important;
      }
    }
    &.gallery-title-picker {
      transform: translate(54px, 0px);
    }
    &.gallery-icon-picker {
      transform: translate(54px, 0px);
    }
    &.gallery-background-picker {
      transform: translate(54px, 0px);
    }
  }
`;

const ColorInputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ColorInputRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ColorPicker = styled.input`
  width: 48px;
  height: 48px;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  cursor: pointer;
  background: transparent;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  &::-webkit-color-swatch-wrapper {
    padding: 0;
  }

  &::-webkit-color-swatch {
    border: none;
    border-radius: 6px;
  }
`;

export const ColorTextInput = styled.input`
  padding: 12px 16px;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 14px;
  color: #1e293b;
  font-family: monospace;
  transition: border-color 0.2s ease;
  width: 200px;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const IconImageSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ChoiceContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
`;

const IconImageRow = styled.div`
  display: grid;
  grid-template-columns: 300px 160px 44px; /* Increased middle column from 140px to 160px */
  gap: 32px;
  align-items: end;
`;

const ButtonSection = styled.div``;
const IconSection = styled.div``;

const ChoiceSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ChoiceButtons = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
`;

const ChoiceButton = styled.button<{ active: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px 16px;
  height: 44px;
  border: 2px solid ${(props) => (props.active ? "#3b82f6" : "#e2e8f0")};
  border-radius: 8px;
  background: ${(props) => (props.active ? "#f0f9ff" : "#ffffff")};
  color: ${(props) => (props.active ? "#3b82f6" : "#64748b")};
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.2s ease;
  min-width: 80px;

  &:hover {
    border-color: #3b82f6;
    background: #f8fafc;
  }
`;

const IconSelectorButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  height: 44px;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  background: #ffffff;
  color: #1e293b;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  width: 100%;

  &:hover {
    border-color: #3b82f6;
    background: #f8fafc;
  }

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const ColoredIconPreview = styled.div<{ bgColor: string; iconColor: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: ${(props) => props.bgColor};
  color: ${(props) => props.iconColor};
`;

const ImageUploadArea = styled.div`
  border: 2px dashed #cbd5e1;
  border-radius: 16px;
  padding: 32px;
  text-align: center;
  background: #ffffff;
  transition: all 0.2s ease;
  cursor: pointer;
  width: 240px;
  min-height: 200px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;

  &:hover {
    border-color: #3b82f6;
    color: #3b82f6;
    background: #f8fafc;
  }
  &.upload-validation {
    border-color: red;
  }
`;

const ImageContainer = styled.div`
  position: relative;
  display: inline-block;
`;

const LargeImagePreview = styled.img`
  max-width: 400px;
  min-height: 200px;
  max-height: 300px;
  border-radius: 8px;
  object-fit: cover;
  border: 2px solid #e2e8f0;
`;

const ImageDeleteButton = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  width: 28px;
  height: 28px;
  border-radius: 6px;
  border: none;
  background: rgba(0, 0, 0, 0.6);
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(0, 0, 0, 0.8);
  }
`;

const OldLogoPreview = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f8fafc;
  border: 2px solid #e2e8f0;
  border-radius: 16px;
  padding: 24px;
  min-height: 120px;
`;

const OldLogoImage = styled.img`
  max-width: 200px;
  max-height: 80px;
  width: auto;
  height: auto;
  object-fit: contain;
`;

const OldLogoSvgContainer = styled.div<{ logoColor?: string }>`
  max-width: 200px;
  max-height: 80px;

  svg {
    width: auto;
    height: auto;
    max-width: 200px;
    max-height: 80px;

    * {
      fill: ${(props) => props.logoColor || "#1e293b"} !important;
    }

    path,
    circle,
    rect,
    polygon,
    ellipse,
    polyline,
    line {
      fill: ${(props) => props.logoColor || "#1e293b"} !important;
    }
  }
`;

const UploadIcon = styled.div`
  color: #94a3b8;
  margin-bottom: 8px;
`;

const UploadText = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #64748b;
  transition: color 0.2s ease;
`;

const UploadSubtext = styled.div`
  font-size: 14px;
  color: #94a3b8;
  transition: color 0.2s ease;
`;

const FileInput = styled.input`
  display: none;
`;

interface GalleryAppearanceSectionProps {
  onSettingsChange: () => void;
  gallerySettings: GallerySettingsModel;
}

export const GalleryAppearanceSection: React.FC<GalleryAppearanceSectionProps> =
  observer(({ onSettingsChange, gallerySettings }) => {
    const [showIconSelector, setShowIconSelector] = useState(false);
    const [showLogoIconColorPicker, setShowLogoIconColorPicker] =
      useState(false);
    const [showTitleColorPicker, setShowTitleColorPicker] = useState(false);
    const [showBackgroundColorPicker, setShowBackgroundColorPicker] =
      useState(false);

    // Add gallery-specific settings to store if they don't exist
    const iconType = gallerySettings.iconType || "None";
    const logoIcon = gallerySettings.icon || "Heart";
    const logoIconColor =
      gallerySettings.iconColor || new ColorModel({ hex: "#10b981" });

    const handleTitleChange = (value: string) => {
      fundraiserStore.updateGallerySettings("title", value);
      onSettingsChange();
    };

    const handleTitleColorChange = (value: IColor) => {
      fundraiserStore.updateGallerySettings("titleColor", value);
      onSettingsChange();
    };

    const handleBackgroundColorChange = (value: IColor) => {
      fundraiserStore.updateGallerySettings("backgroundColor", value);
      onSettingsChange();
    };

    const handleLogoClick = () => {
      fundraiserStore.updateGallerySettings("showLibrary", true);
      onSettingsChange();
    };

    const removeImage = () => {
      fundraiserStore.updateGallerySettings("iconMedia", null);
      onSettingsChange();
    };

    const handleLogoChoiceChange = (choice: string) => {
      fundraiserStore.updateGallerySettings("iconType", choice);
      if (choice === IconType.None || choice === IconType.Icon) {
        fundraiserStore.updateGallerySettings("iconMedia", null);
      }
      onSettingsChange();
    };

    const handleLogoIconChange = (icon: string) => {
      fundraiserStore.updateGallerySettings("icon", icon);
      onSettingsChange();
    };

    const handleLogoIconColorChange = (color: IColor) => {
      fundraiserStore.updateGallerySettings("iconColor", color);
      onSettingsChange();
    };

    const handleTitleColorInput = (value: ColorModel) => {
      fundraiserStore.updateGallerySettings("titleColor", value);
      onSettingsChange();
    };

    const handleBgColorInput = (value: ColorModel) => {
      fundraiserStore.updateGallerySettings("backgroundColor", value);
      onSettingsChange();
    };

    const getIconColors = (selectedColor: string) => {
      const bgColor = selectedColor;
      const iconColor = isLightColor(selectedColor)
        ? darkenColor(selectedColor)
        : "#ffffff";
      return { bgColor, iconColor };
    };

    const renderIcon = (iconName: string) => {
      const Icon = (Icons as any)[iconName];
      return Icon ? <Icon size={16} /> : <Heart size={16} />;
    };

    return (
      <Section>
        <SectionTitle>
          <Palette size={24} />
          Appearance
        </SectionTitle>

        <IconImageSection>
          <ChoiceContainer>
            <IconImageRow>
              <ButtonSection>
                <Label>Icon / Logo</Label>
              </ButtonSection>
              {iconType === IconType.Icon && (
                <>
                  <IconSection>
                    <Label>Icon</Label>
                  </IconSection>
                  <ColorSection>
                    <Label>Color</Label>
                  </ColorSection>
                </>
              )}
            </IconImageRow>
            <IconImageRow>
              <ButtonSection>
                <ChoiceSection>
                  <ChoiceButtons>
                    <ChoiceButton
                      active={iconType === IconType.None}
                      onClick={() => handleLogoChoiceChange("None")}
                    >
                      None
                    </ChoiceButton>
                    <ChoiceButton
                      active={iconType === IconType.Icon}
                      onClick={() => handleLogoChoiceChange("Icon")}
                    >
                      Icon
                    </ChoiceButton>
                    <ChoiceButton
                      active={iconType === IconType.Image}
                      onClick={() => handleLogoChoiceChange("Image")}
                    >
                      Logo
                    </ChoiceButton>
                  </ChoiceButtons>
                </ChoiceSection>
              </ButtonSection>

              {iconType === IconType.Icon && (
                <>
                  <IconSection>
                    <IconSelectorButton
                      onClick={() => setShowIconSelector(true)}
                    >
                      <ColoredIconPreview
                        bgColor={
                          getIconColors(logoIconColor.rgba || logoIconColor.hex)
                            .bgColor
                        }
                        iconColor={
                          getIconColors(logoIconColor.rgba || logoIconColor.hex)
                            .iconColor
                        }
                      >
                        {renderIcon(logoIcon)}
                      </ColoredIconPreview>
                      {logoIcon}
                    </IconSelectorButton>
                  </IconSection>

                  {/* Custom Color Picker Section (replaces <ColorPicker type="color" />) */}
                  <ColorSection className="position-relative">
                    <FlexContainer
                      spacing={10}
                      alignItems="center"
                      className="position-relative test1"
                    >
                      {/* Color Button */}
                      <SelectedColorButton
                        heightWidth="24px"
                        borderRadius="2px"
                        className="color-picker-gallery "
                        onClick={() =>
                          setShowLogoIconColorPicker(!showLogoIconColorPicker)
                        }
                        color={logoIconColor.rgba || logoIconColor.hex}
                      >
                        <div />
                      </SelectedColorButton>
                      {showLogoIconColorPicker && gallerySettings.iconColor && (
                        <ColorPickerComponent
                          color={gallerySettings.deserializeColor(
                            gallerySettings.iconColor
                          )}
                          onChange={(color) => handleLogoIconColorChange(color)}
                          onHide={() => setShowLogoIconColorPicker(false)}
                          className="checkout-color-picker gallery-icon-picker"
                          // squareHeight="150px"
                        />
                      )}
                      {/* Color Text Input */}
                      {/* Color Picker Popup */}
                    </FlexContainer>
                  </ColorSection>
                </>
              )}
            </IconImageRow>

            {iconType === IconType.Image && (
              <div>
                {gallerySettings.iconMedia.fullURL ? (
                  <ImageContainer>
                    <LargeImagePreview
                      src={gallerySettings.iconMedia.fullURL}
                      alt="Gallery Logo"
                      style={{ cursor: "pointer" }}
                    />
                    <ImageDeleteButton
                      onClick={(e) => {
                        e.stopPropagation();
                        removeImage();
                      }}
                    >
                      <X size={14} />
                    </ImageDeleteButton>
                  </ImageContainer>
                ) : (
                  <>
                    <ImageUploadArea
                      onClick={() => handleLogoClick()}
                      className={
                        gallerySettings.hasError("iconMedia")
                          ? "upload-validation"
                          : ""
                      }
                    >
                      <UploadIcon>
                        <Plus size={32} />
                      </UploadIcon>
                      <UploadText>Add Logo</UploadText>
                      <UploadSubtext>Select or upload an image</UploadSubtext>
                    </ImageUploadArea>
                    {/* <p style={{ color: "red" }}>
                      {gallerySettings.hasError("iconMedia") &&
                        "Icon media is required"}
                    </p> */}
                  </>
                )}
              </div>
            )}
          </ChoiceContainer>
        </IconImageSection>

        <FormGroup>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              width: "400px",
              marginBottom: "8px",
            }}
          >
            <Label htmlFor="galleryTitle">Gallery Title</Label>
            <CharacterCounter>
              {(gallerySettings.title || "").length} of 25 characters
            </CharacterCounter>
          </div>
          <Input
            id="galleryTitle"
            type="text"
            style={
              gallerySettings.hasError("title") ? { borderColor: "red" } : {}
            }
            placeholder="Enter gallery title"
            maxLength={25}
            value={gallerySettings.title}
            onChange={(e) => handleTitleChange(e.target.value)}
          />
        </FormGroup>

        <ColorSection>
          <ColorInputGroup>
            <Label>Title Color</Label>
            <ColorInputRow className="position-relative">
              <FlexContainer
                spacing={10}
                alignItems="center"
                className="position-relative"
              >
                <SelectedColorButton
                  heightWidth="44px"
                  borderRadius="8px"
                  onClick={() => setShowTitleColorPicker(!showTitleColorPicker)}
                  color={
                    gallerySettings.titleColor.rgba ||
                    gallerySettings.titleColor.hex
                  }
                  className="color-picker-gallery"
                >
                  <div />
                </SelectedColorButton>
                <ColorTextInput
                  type="text"
                  value={gallerySettings.titleColor.hex}
                  onChange={(e) =>
                    handleTitleColorInput(
                      new ColorModel({
                        hex: e.target.value,
                        rgba: "",
                      })
                    )
                  }
                  onBlur={(e) => {
                    handleTitleColorInput(
                      ColorModel.fromString(e.target.value)
                    );
                  }}
                  placeholder="#1e293b"
                  onClick={(e) => e.stopPropagation()}
                />

                {/* Color Picker Popup */}
                {showTitleColorPicker && (
                  <ColorPickerComponent
                    color={gallerySettings.deserializeColor(
                      gallerySettings.titleColor
                    )}
                    onChange={(color) => handleTitleColorChange(color)}
                    onHide={() => setShowTitleColorPicker(false)}
                    className="checkout-color-picker gallery-title-picker"
                    // squareHeight="150px"
                  />
                )}
              </FlexContainer>
            </ColorInputRow>
          </ColorInputGroup>

          <ColorInputGroup>
            <Label>Background Color</Label>
            <ColorInputRow style={{ position: "relative" }}>
              <FlexContainer
                spacing={10}
                alignItems="center"
                // className="position-relative"
              >
                <SelectedColorButton
                  heightWidth="24px"
                  borderRadius="2px"
                  className="color-picker-gallery"
                  onClick={() =>
                    setShowBackgroundColorPicker(!showBackgroundColorPicker)
                  }
                  color={
                    gallerySettings.backgroundColor.rgba ||
                    gallerySettings.backgroundColor.hex
                  }
                >
                  <div />
                </SelectedColorButton>
                <ColorTextInput
                  type="text"
                  value={gallerySettings.backgroundColor.hex}
                  onChange={(e) =>
                    handleBgColorInput(
                      new ColorModel({
                        hex: e.target.value,
                        rgba: "",
                      })
                    )
                  }
                  onBlur={(e) => {
                    handleBgColorInput(ColorModel.fromString(e.target.value));
                  }}
                  placeholder="#1e293b"
                  onClick={(e) => e.stopPropagation()}
                />
                {showBackgroundColorPicker && (
                  <ColorPickerComponent
                    color={gallerySettings.deserializeColor(
                      gallerySettings.backgroundColor
                    )}
                    onChange={(color) => handleBackgroundColorChange(color)}
                    onHide={() => setShowBackgroundColorPicker(false)}
                    className="checkout-color-picker gallery-background-picker"
                  />
                )}
              </FlexContainer>
            </ColorInputRow>
          </ColorInputGroup>
        </ColorSection>

        <IconSelectorModal
          visible={showIconSelector}
          selectedIcon={logoIcon}
          onClose={() => setShowIconSelector(false)}
          onSelect={(iconName) => {
            handleLogoIconChange(iconName);
            setShowIconSelector(false);
          }}
        />
      </Section>
    );
  });
